import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nds from '@nerdwallet/base-styles/classes';
import { Box, Title, Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import {
  stringInputSpec,
  stringTextAreaSpec,
} from '../../../../lib/editing-configs';
import parseStyleFormatting from '../parse-awards-2023-formatting';
import styles from './partner-page.module.less';
import { useScrollAnimation } from '../useScrollAnimation';

const PartnerPage = ({
  title,
  subtitle,
  brandLogoUrl,
  brandLogoLink,
  topLeftIconUrl,
  topRightIconUrl,
  topLeftIconUrlMobile,
  bottomLeftIconUrl,
}) => {
  const desktopLargeStarRef = useRef();
  const desktopSmallStarRef = useRef();
  const mobileLargeStarRef = useRef();
  const mobileSmallStarRef = useRef();
  const desktopRightSmallStarRef = useRef();

  useScrollAnimation([
    { elementRef: desktopLargeStarRef, speed: 5 },
    { elementRef: desktopSmallStarRef, speed: 2 },
    { elementRef: mobileLargeStarRef, speed: 2 },
    { elementRef: mobileSmallStarRef, speed: 5, animationType: 'bounce' },
    { elementRef: desktopRightSmallStarRef, speed: 2, animationType: 'bounce' },
  ]);

  return (
    <Box className={styles.container}>
      <Box className={styles.awardsHero2023BrandLogo}>
        {topLeftIconUrlMobile && (
          <Box className={styles.awardsHero2023LeftStarsMobile}>
            {topLeftIconUrlMobile && (
              <Box ref={mobileLargeStarRef}>
                <Image
                  naturalHeight={150}
                  naturalWidth={150}
                  backgroundColor="transparent"
                  src={topLeftIconUrlMobile}
                  alt="big star"
                />
              </Box>
            )}
            {bottomLeftIconUrl && (
              <Box
                ref={mobileSmallStarRef}
                style={{ maxWidth: 31, alignSelf: 'flex-end' }}
                className={classNames([nds('text-align-right')])}
              >
                <Image
                  naturalHeight={31}
                  naturalWidth={31}
                  backgroundColor="transparent"
                  src={bottomLeftIconUrl}
                  alt="pink star"
                />
              </Box>
            )}
          </Box>
        )}

        {brandLogoUrl && (
          <Box className={styles.awardsHero2023LogoWrapper}>
            {brandLogoLink ? (
              <Link href={brandLogoLink}>
                <Image
                  naturalHeight={244}
                  naturalWidth={313}
                  backgroundColor="transparent"
                  src={brandLogoUrl}
                  alt="brand logo"
                />
              </Link>
            ) : (
              <Image
                naturalHeight={244}
                naturalWidth={313}
                backgroundColor="transparent"
                src={brandLogoUrl}
                alt="brand logo"
              />
            )}
          </Box>
        )}
      </Box>

      <Box className={styles.awardsHero2023Main}>
        {topLeftIconUrl && (
          <Box className={styles.awardsHero2023LeftStarsLarge}>
            {topLeftIconUrl && (
              <Box ref={desktopLargeStarRef}>
                <Image
                  naturalHeight={150}
                  naturalWidth={150}
                  backgroundColor="transparent"
                  src={topLeftIconUrl}
                  alt="big star"
                />
              </Box>
            )}
            {bottomLeftIconUrl && (
              <Box
                ref={desktopSmallStarRef}
                style={{ maxWidth: 62, alignSelf: 'flex-end' }}
                className={classNames([nds('text-align-right')])}
              >
                <Image
                  naturalHeight={62}
                  naturalWidth={62}
                  backgroundColor="transparent"
                  src={bottomLeftIconUrl}
                  alt="pink star"
                />
              </Box>
            )}
          </Box>
        )}

        <Box className={styles.awardsHero2023TextContainer}>
          <Box
            display="flex"
            flexDirection="column"
            className={classNames([nds('margin-vertical-3')])}
          >
            <Title className={styles.awardsHero2023TextTitle}>
              {parseStyleFormatting(title)}
            </Title>
            <Box className={styles.awardsHero2023TextSubtitle}>
              <Text component="p">{subtitle}</Text>
            </Box>
          </Box>
          {topRightIconUrl && (
            <Box
              ref={desktopRightSmallStarRef}
              className={styles.awardsHero2023RightStars}
            >
              <Image
                backgroundColor="transparent"
                naturalHeight={35}
                naturalWidth={35}
                src={topRightIconUrl}
                alt="pink star"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

PartnerPage.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  brandLogoLink: PropTypes.string,
  topLeftIconUrl: PropTypes.string,
  bottomLeftIconUrl: PropTypes.string,
  topRightIconUrl: PropTypes.string,
  topLeftIconUrlMobile: PropTypes.string,
};

PartnerPage.defaultProps = {
  brandLogoUrl: '',
  brandLogoLink: '',
};

PartnerPage.getEditingConfig = () => {
  return [
    stringTextAreaSpec('title', 'Title', {
      help: 'For Gotham black font surround with a | like |this|, For italics, surround title text with ^ like ^this^. To include a linebreak, insert :br: in the textbox',
    }),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    stringInputSpec('brandLogoUrl', 'Brand Logo URL'),
    stringInputSpec('brandLogoLink', 'Brand Logo Link'),
    stringInputSpec('topLeftIconUrl', 'Top Left Image URL'),
    stringInputSpec('topLeftIconUrlMobile', 'Top Left Image URL Mobile'),
    stringInputSpec('bottomLeftIconUrl', 'Bottom Left Image URL'),
    stringInputSpec('topRightIconUrl', 'Top Right Image URL'),
  ];
};

export default PartnerPage;
